// ----------------------------------------------------------------------
// <copyright file="Izenda.Table.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import ContextMenu from "./Izenda.ContextMenu";

/**
 * @module Table component
 * @example
 *  <Table dataSource={array}                   // data of table
 rowProps={function}                  // set properties for each row based on its content
 onSortChange={function}              // [if sort is used] on sort call back
 sortInfo={object}                    // sort infor of each column
 >

 <Column cell={function|component} {...other property} />
 *  </Table>
 */
!(function() {
  var observer = require('mobx-react').observer;
  var React = require('react');
  var PropTypes = React.PropTypes;
  var MobxPropTypes = require('mobx-react').propTypes;
  var classnames = require('classnames');
  var elementResizeDetector = require('share/utils/Izenda.SizeDetector');
  var DomUtils = require('share/utils/Izenda.DomUtils');
  var BrowserUtils = require('share/utils/Izenda.BrowserUtils');

  var VirtualList = require('share/components/controls/Izenda.VirtualList');
  var Resizer = require('share/components/controls/Izenda.Resizer');
  var ScrollBox = require('share/components/controls/Izenda.ScrollBox');
  var Cell = require('./Izenda.TableCell');
  var Column = require('./Izenda.TableColumn');
  var ColumnGroup = require('./Izenda.TableColumnGroup');
  var Row = require('./Izenda.TableRow');
  var HeaderCell = require('./Izenda.TableHeaderCell');
  var DraggableRow = require('./Izenda.DraggableRow');
  var TableUIStore = require('./Izenda.TableUIStore');
  var ResizeHandleWidth = 21;
  var localizer = require('share/localization/Izenda.Localizer');
  var RowSpanColumn = require('./Izenda.RowSpanColumn');
  var RowSpanCell = require('./Izenda.RowSpanCell');
  var ContextMenu = require('./Izenda.ContextMenu');

  var GroupHeaderCell = require('./Izenda.GroupHeaderCell');
  const {
    memoize,
    map,
    get,
    isNil,
    curry,
    debounce,
    compact,
    assign,
    constant,
    isEmpty,
    invoke,
    pick,
    flattenDeep,
    flatMap,
    concat,
    getOr,
    join,
    forEach,
    omit,
    isFunction,
    identity,
    noop
  } = require('lodash/fp');
  var Table = React.createClass({
    isHeightUpdated: false,
    userResizing: false,
    newWidths: [],
    columnHeaderCounter: 0,
    maxDeepHeader: 0,
    headerGroups: [],
    // debouce function to make sure only run after render function at minimum time
    propTypes: {
      removeHeaderForViewer: PropTypes.bool,
      showScroll: PropTypes.bool,
      minHeight: PropTypes.number,
      maxHeight: PropTypes.number,
      dataSource: MobxPropTypes.arrayOrObservableArray,
      getChildItems: PropTypes.func,
      isHighLight: PropTypes.func,
      containerWidth: PropTypes.number,
      autoResize: PropTypes.bool,
      selectCell: PropTypes.object,
      defaultColumnWidth: PropTypes.number,
      afterRenderCallBack: PropTypes.func,
      rowSpan: PropTypes.shape({
        data: PropTypes.array.isRequired,
        width: PropTypes.number,
        cell: PropTypes.func.isRequired,
        fnGetCellStyle: PropTypes.func,
        fnGetCellContentStyle: PropTypes.func
      }),
      isLoading: PropTypes.bool,
      loadingText: PropTypes.string,
      staticRender: PropTypes.bool // static render table rows instead of VirtualList
    },

    getDefaultProps: function() {
      return {
        removeHeaderForViewer: false,
        showScroll: true,
        minHeight: 238,
        maxHeight: 238,
        defaultColumnWidth: 150,
        autoResize: true,
        containerWidth: Infinity,
        isLoading: false,
        loadingText: '',
        selectCell: {
          rowIndex: -1,
          columnIndex: -1
        }
      };
    },

    componentWillMount: function() {
      var props = this.props;
      var showScroll = props.showScroll;
      var tableHeight = props.minHeight;
      var autoResize = props.autoResize;
      var defaultColumnWidth = props.defaultColumnWidth;

      this.tableStore = new TableUIStore(
        defaultColumnWidth,
        showScroll ? tableHeight : 0,
        autoResize,
        this.onTableWidthChanged,
      );

      this.initColumnSizes(props, false);
      this.registerRenderCallback(props);

      this.memoizeRegisterHeaderGroup = memoize(
        function(headerGroupId) {
          return curry(this.registerHeaderGroupCell)(headerGroupId);
        }.bind(this)
      );

      this.memoizeUnregisterHeaderGroup = memoize(
        function(headerGroupId) {
          return curry(this.unregisterHeaderGroupCell)(headerGroupId);
        }.bind(this)
      );

      document.removeEventListener('click', this.handleOutsideClick);

      this.isSupportSticky = BrowserUtils.isSupportSticky();
    },

    componentWillUpdate: function(nextProps) {
      if (nextProps.children !== this.props.children) {
        this.initColumnSizes(nextProps, true);
      }
      if (this.props.afterRenderCallBack !== nextProps.afterRenderCallBack) {
        this.registerRenderCallback(nextProps);
      }

      if (nextProps.showScroll
        && nextProps.minHeight > 0
        && (this.tableStore.tableHeight <= 0 || this.tableStore.tableHeight !== nextProps.minHeight)
      ) {
        this.tableStore.updateHeight(nextProps.minHeight);
      }
    },

    finishedRender: function(prevProps) {
      var props = this.props;
      var tableElement = this.refs.table;
      var handleSize = props.draggableTable ? ResizeHandleWidth : 0;

      if (
        props.containerWidth === Infinity &&
        props.autoResize &&
        (isNil(prevProps) || prevProps.children !== props.children)
      ) {
        var width = $izenda(tableElement).width();

        this.changeTableWidth(Math.floor(width) - handleSize, props.children, true);
      }

      if (props.minHeight > 0 && props.showScroll && !this.isHeightUpdated) {
        this.tableStore.updateHeight(props.minHeight, this.refs.headerTable.clientHeight);
        this.isHeightUpdated = true;
      }
      this.headerGroupAfterRenderCallBack();
      this.headerAfterRenderCallBack();
      this.cellAfterRenderCallBack();
      this.afterRenderCallBack();

      if (props.stickHorizontal) {
        const bodyWidth = this.refs.tableBody.offsetWidth;
        bodyWidth > 0 && tableElement.style.setProperty('width', bodyWidth + 'px');
      }

      if (!this.isSupportSticky) {
        // Sync size of virtual header if exist
        const headerTable = this.refs.headerTable;
        const parentScrollNode = DomUtils.findScrollableParent(tableElement);
        const virtualHeaderTable = this.refs.virtualHeaderTable;
        if (virtualHeaderTable) {
          virtualHeaderTable.style.setProperty('width', headerTable.offsetWidth + 'px');
          virtualHeaderTable.style.setProperty('height', headerTable.offsetHeight + 'px');
        }

        if (props.stickVertical || props.stickHorizontal) {
          let lastScrollTop = 0,
            lastScrollLeft = 0;
          $izenda(parentScrollNode).on('scroll', event => {
            const currentNode = event.target;
            if (lastScrollTop !== currentNode.scrollTop && props.stickVertical) {
              lastScrollTop = currentNode.scrollTop;
              this.tableStore.updateTopPosition(currentNode, tableElement);
            }

            if (lastScrollLeft !== currentNode.scrollLeft && props.stickHorizontal) {
              lastScrollLeft = currentNode.scrollLeft;
              this.tableStore.updateLeftPosition(currentNode, tableElement);
            }
          });
        }

        props.stickVertical
          ? this.tableStore.updateTopPosition(parentScrollNode, tableElement)
          : this.tableStore.resetTopPosition();

        props.stickHorizontal
          ? this.tableStore.updateLeftPosition(parentScrollNode, tableElement)
          : this.tableStore.resetLeftPosition();
      }
    },

    componentDidMount: function() {
      var props = this.props;
      var tableElement = this.refs.table;
      var handleSize = props.draggableTable ? ResizeHandleWidth : 0;

      if (props.containerWidth === Infinity && props.autoResize) {
        elementResizeDetector.listenTo(
          tableElement,
          debounce(
            250,
            function() {
              var width = $izenda(tableElement).width();
              // Only update table width when the container has been resized.

              !this.userResizing && this.changeTableWidth(Math.floor(width) - handleSize, this.props.children, true);
            }.bind(this)
          )
        );
      }

      document.addEventListener('click', this.handleOutsideClick);
      this.finishedRender();
    },

    componentDidUpdate: function(prevProps) {
      this.finishedRender(prevProps);
    },

    componentWillUnmount: function() {
      var tableElement = this.refs.table;

      if (this.props.containerWidth === Infinity && this.props.autoResize) {
        elementResizeDetector.removeAllListeners(tableElement);
      }
    },

    render: function() {
      const {tableStore, props} = this;
      const {
        showScroll,
        stickVertical,
        stickHorizontal,
        className,
        containerWidth,
        children,
        tableStyle,
        draggableTable,
        rowSpan,
        defaultColumnWidth,
        removeHeaderForViewer,
        sourceForm
      } = props;

      const columns = compact(React.Children.toArray(children));
      const autoWidth = containerWidth === Infinity;
      const tableStyleFinal = assign(tableStyle, autoWidth ? {width: '100%'} : null);
      //default style adding
      let classes = classnames('table', className, {
        'default-table': !className
      });

      if(sourceForm === 'report-part-grid' || sourceForm === 'report-part-grid-pivot') {
        tableStyleFinal.border = '1px solid #dbdcdd';
        tableStyleFinal.borderRadius = '10px';
        tableStyleFinal.width = 'fit-content';
        classes = classnames(classes, 'table-new-grid')
      }
      const headerClasses = classnames('table-header', {
        'u-stickTop': stickVertical
      });

      tableStore.resetHeaderGroups();
      this.columnHeaderCounter = 0;
      var menuSetting = this.tableStore.menuSetting;

      return (
        <section ref="table" className={classes} style={tableStyleFinal}>
          {stickVertical && !this.isSupportSticky && <div className="u-posAbsolute u-virtualStickTop" />}

          {/*Table Header*/}
          {!removeHeaderForViewer && (
            <div className={headerClasses} ref="headerTable">
              <div className="table-row">
                {draggableTable && <div className="table-cell u_paddingAll" />}
                {rowSpan && (
                  <RowSpanCell width={rowSpan.width || defaultColumnWidth} cell={constant(null)} handleItemClick={this.handleItemClick} sourceForm={props.sourceForm} selectCell={this.tableStore.selectCell} />
                )}
                {stickHorizontal && !this.isSupportSticky && <div className="u-virtualStickLeft" />}
                {columns.map(this.renderHeaderCells.bind(this, 1, []))}
              </div>
            </div>
          )}

          {/*Table Body*/}
          {showScroll ? (
            <ScrollBox onScroll={this.handleScroll} containerStyle={{ height: tableStore.bodyHeight }}>
              {this.renderBody()}
            </ScrollBox>
          ) : (
            this.renderBody()
          )}
          <ContextMenu
            isVisible={menuSetting.isVisible}
            mouseEvent={menuSetting.mouseEvent}
            root={this}
            cellInfor={menuSetting.cellInfor}
            needUpdatePreview={props.needUpdatePreview}
            reportPart={props.reportPart}
            previewRecords={props.previewRecords}
            ref={(e) => this.elm = e} item={'Item 1'}/>
        </section>
      );
    },

    renderBody: function() {
      var props = this.props;
      var tableBody = this.renderTableBody();

      if (props.rowSpan && !props.staticRender) {
        const classes = classnames('table-cell', {
          'u-stickLeft': props.stickHorizontal
        });
        return (
          <div className="table-row" ref="tableBody">
            {props.stickHorizontal && !this.isSupportSticky && <div className="u-virtualStickLeft" />}
            <div className={classes}>
              <RowSpanColumn
                tableStore={this.tableStore}
                rowSpan={props.rowSpan}
                width={props.rowSpan.width || props.defaultColumnWidth}
              />
            </div>
            <div className="table-cell">{tableBody}</div>
          </div>
        );
      }

      return tableBody;
    },

    disableSyncHeaderHeightOnce: function() {
      // We don't need to update header heights when cell is selected
      // and we'll set those variables to false on first headerGroupAfterRenderCallBack and
      // headerAfterRenderCallBack calls and headers will be ready to syncHeight again if needed.
      this.skipSyncHeaderHeight = true;
      this.skipSyncGroupHeaderHeight = true;
    },

    handleOutsideClick: function(event) {
      const { isVisible, sourceFlag } = this.tableStore.menuSetting;
      const wasOutside = !(event.target.contains === this.elm);
      var menuSetting = this.tableStore.menuSetting;
      if (wasOutside && !sourceFlag && isVisible) {
        menuSetting.isVisible = false;
        menuSetting.sourceFlag = false;
        this.disableSyncHeaderHeightOnce();
        this.tableStore.updateMenuSetting(menuSetting);
      }
      menuSetting.sourceFlag = false;
    },

    toggleContextMenu: function(rootElm) {
      const mouseEvent = rootElm.nativeEvent;
      var menuSetting = this.tableStore.menuSetting;
      let newSetting = {
        isVisible: !menuSetting.isVisible,
        mouseEvent,
        sourceFlag: false
      }

      this.tableStore.updateMenuSetting(newSetting)
    },

    openMenu: function(event) {
      event.preventDefault();
      this.toggleContextMenu(event);
    },

    renderTableBody: function() {
      var getChildItems = this.props.getChildItems;
      var dataSource = this.props.dataSource;
      var selectCell = this.tableStore.selectCell;
      var sourceForm = this.props.sourceForm

      var selectStyle = {
        left: (selectCell.left - 1) + 'px',
        top: (selectCell.top - 1) + 'px',
        width: selectCell.width + 'px',
        height: selectCell.height + 'px',
      }

      var tableContainerStyle = {
        position: 'relative'
      };

      // if(selectCell.rowIndex >= 0 && selectCell.columnIndex >= 0) {
      //   tableContainerStyle.border = '1px solid #2b7cbd';
      // }

      return (
        <div className="table-body" ref="tableBody" style={tableContainerStyle}>
          {isEmpty(dataSource) ? (
            this.renderNoRecordFound()
          ) : this.props.staticRender ? (
            this.renderRows(dataSource)
          ) : (
            <VirtualList
              bufferClassName="table-row"
              dataSource={dataSource}
              renderItem={this.renderRow}
              getItemKey={invoke('getId')}
              getChildItems={getChildItems}
              registerVirtualStore={this.registerVirtualStore}
            />
          )}
        </div>
      );
    },

    renderNoRecordFound: function() {
      var noRecordText = this.props.isLoading
        ? this.props.loadingText
        : localizer.getText('COMMON_MESSAGES_NO_RECORD_FOUND');

      return <div className="u_paddingAll">{noRecordText}</div>;
    },

    staticRenderRowSpan: function(dataRow) {
      var props = this.props;
      var rowSpan = get('rowSpan', props);
      var firstRow = 0;
      var pickFn = pick(['fnGetCellStyle', 'fnGetCellContentStyle', 'cell'], rowSpan);
      var mapConvert = map.convert({ cap: false });

      return compact(
        mapConvert(function(rowSpanElement, index) {
          var lastRow = firstRow + rowSpanElement.size;
          var listRow = dataRow.slice(firstRow, lastRow);

          firstRow = lastRow;
          if (isEmpty(listRow)) {
            return null;
          }
          return (
            <div key={firstRow} className="table-row">
              <RowSpanCell
                rowSpan={rowSpanElement}
                width={rowSpan.width || props.defaultColumnWidth}
                {...pickFn}
                index={index}
                handleItemClick={this.handleItemClick}
                selectCell={this.tableStore.selectCell}
                sourceForm={props.sourceForm}
              />
              <div className="table-cell">{listRow}</div>
            </div>
          );
        }, rowSpan.data)
      );
    },

    renderRows: function(dataSource) {
      var props = this.props;

      dataSource = dataSource || [];

      var dataRow = flattenDeep(
        dataSource.map(
          function(item, index) {
            var result = this.renderRow(item, index);

            return forEach(identity, result);
          }.bind(this)
        )
      );

      if (get('rowSpan', props)) {
        return this.staticRenderRowSpan(dataRow);
      }
      return dataRow;
    },

    renderRow: function(item, index) {
      var isHiddenRowFn = getOr(null, 'currentRow.isHiddenRowFn', item);
      if (isHiddenRowFn && isHiddenRowFn())
        return null;

      var props = this.props;
      var columns = this.getAllColumns(props.children);
      var widths = this.tableStore.getActualColumnWidths();

      var RowComponent = props.draggableTable ? DraggableRow : Row;

      return (
        <RowComponent
          key={(item.getId && item.getId()) || index}
          rowSpan={props.rowSpan}
          rowProps={props.rowProps}
          rowData={item}
          columns={columns}
          columnWidths={widths}
          move={props.move}
          canDrop={props.canDrop}
          index={index}
          canDrag={props.canDrag}
          warning={props.warning}
          onClickRow={props.onClickRow}
          isHighLight={props.isHighLight}
          didUpdate={this.afterRenderCallBack}
          registerCell={this.registerCell}
          unregisterCell={this.unregisterCell}
          stickHorizontal={props.stickHorizontal}
          isSupportSticky={this.isSupportSticky}
          tableStore={this.tableStore}
          cellAfterRenderCallBack={this.cellAfterRenderCallBack}
          handleItemClick={this.handleItemClick}
          sourceForm={props.sourceForm}
          selectCell={this.tableStore.selectCell}
        />
      );
    },

    handleCellUpdate: function(event) {
      let rectTable = this.tableStore.selectCell.tableBody;
      if(rectTable.left < event.clientX && event.clientX < rectTable.right && rectTable.top < event.clientY && event.clientY > rectTable.bottom) {

      } else {
        this.tableStore.updateSelectCell({
          rowIndex: -1,
          columnIndex: -1,
          top: -1,
          left: -1,
          width: 0,
          height: 0
        });
      }
    },

    handleMenuClick: function(columnIndex, position, cellInfor) {
      var menuSetting = this.tableStore.menuSetting;
      var mouseEvent = {clientX: position.x, clientY: position.y}

      menuSetting.isVisible = true;
      menuSetting.mouseEvent = mouseEvent;
      menuSetting.sourceFlag = true;
      menuSetting.cellInfor = cellInfor;
      this.disableSyncHeaderHeightOnce();
      this.tableStore.updateMenuSetting(menuSetting);
    },

    handleItemClick: function(cellInfor) {
      let rect = this.refs.tableBody.getBoundingClientRect();
      cellInfor.top = cellInfor.top - rect.top;
      cellInfor.left = cellInfor.left - rect.left;
      cellInfor.tableBody = rect;
      this.disableSyncHeaderHeightOnce();
      this.tableStore.updateSelectCell(cellInfor);
    },

    renderHeaderCells: function(deep, path, column, index) {
      // Don't render null column
      if (!column) {
        return null;
      }
      const { props } = this;
      const { dataSource } = props;
      const tableStore = this.tableStore;
      const columnProps = column.props;
      const restColumnProps = omit(
        ['width', 'title', 'getTitle', 'resizable', 'fnGetCellStyle', 'fnGetCellContentStyle'],
        columnProps
      );
      const allowResize = columnProps.resizable;

      let subHeaderProps = {};
      let subDrillDownProps = {};
      if (dataSource && dataSource.length) {
        const rowDataObject = getOr(null, 'currentRow.rowDataObject', dataSource[0]);
        if (rowDataObject) {
          const subKeyPath = rowDataObject.subKeyPath || null;
          if (subKeyPath && subKeyPath.length > index) {
            subHeaderProps = subKeyPath[index];
          }
          if (rowDataObject.childs && rowDataObject.childs.length) {
            const subKeyPathChilds = getOr(null, 'childs[0].subKeyPath', rowDataObject);
            if (subKeyPathChilds && subKeyPathChilds.length > index) {
              subDrillDownProps = subKeyPathChilds[index];
            }
          }
        }
      }

      if (deep > this.maxDeepHeader) {
        this.maxDeepHeader = deep;
      }
      var renderTitle = function(callBack) {
        var generatedTitle =
          column.props.title || (isFunction(column.props.getTitle) && column.props.getTitle(callBack));
        return generatedTitle;
      };

      var headerCell = props.sortInfo ? (
        <HeaderCell {...props} {...columnProps} />
      ) : (
        <GroupHeaderCell
          infoIconOptions={columnProps.infoIconOptions}
          didUpdate={this.headerAfterRenderCallBack}
          renderTitle={renderTitle}
        />
      );

      var HeaderComponent = columnProps.headerComponent;
      var headerComponentProps = columnProps.headerComponentProps;
      var currentColumn = this.columnHeaderCounter;
      var columnWidth = tableStore.getColumnWidth(this.columnHeaderCounter);
      var columnMinWidth = columnProps.width;

      if (column.type === ColumnGroup) {
        var classesGroupHeader = classnames({
          'table-cell-group-header': true,
          'table-content-hidden': !!column.props.isInvisible
        });
        var supPath = concat(path, index);
        var refsGroupHeader = {
          path: supPath,
          ref: join('_', supPath),
          startColumn: this.columnHeaderCounter,
          index: index,
          deep: deep
        };

        var child = concat([], column.props.children).map(this.renderHeaderCells.bind(this, deep + 1, supPath));

        refsGroupHeader.endColumn = this.columnHeaderCounter;

        tableStore.addHeaderGroup(refsGroupHeader);

        var width = tableStore.calculateHeaderGroupWidth(refsGroupHeader);

        var groupHeaderCell = !getOr(true, 'props.canRenderHeader', column) ? null : (
          <div className={classesGroupHeader}>
            <Cell
              columnIndex={index}
              fnGetCellStyle={columnProps.fnGetCellStyle}
              fnGetCellContentStyle={columnProps.fnGetCellContentStyle}
              width={width}
              didUpdate={this.headerGroupAfterRenderCallBack}
              registerCell={this.memoizeRegisterHeaderGroup(refsGroupHeader.ref)}
              unregisterCell={this.memoizeUnregisterHeaderGroup(refsGroupHeader.ref)}
            >
              <span className="table-cell-group-title">
                {columnProps.iconExpandCollapse}
                <GroupHeaderCell
                  infoIconOptions={columnProps.infoIconOptions}
                  didUpdate={this.headerGroupAfterRenderCallBack}
                  renderTitle={renderTitle}
                />
              </span>
            </Cell>
          </div>
        );

        const classes = classnames('table-cell-group', {
          'u-stickLeft': props.stickHorizontal && column.props.isColumnHeader
        });

        return (
          <div key={index} className={classes}>
            <div className="nested-table">
              {groupHeaderCell}
              <div className="table-row">{child}</div>
            </div>
          </div>
        );
      }

      if (column.type !== Column) {
        return null;
      }
      var onResize = this.handleColumnResize(this.columnHeaderCounter);
      var onResizeStop = this.handleResizeStop(this.columnHeaderCounter);

      this.columnHeaderCounter++;

      return allowResize ? (
        <Resizer
          key={index}
          width={columnWidth}
          rightResize
          minConstraints={[props.defaultColumnWidth, 0]}
          onResize={onResize}
          onResizeStop={onResizeStop}
        >
          <Cell
            fnGetCellContentStyle={columnProps.fnGetHeaderCellContentStyle}
            fnGetCellStyle={columnProps.fnGetHeaderCellStyle}
            type={"header-cell"}
            deep={deep}
            columnIndex={currentColumn}
            registerCell={this.registerHeaderCell}
            unregisterCell={this.unregisterHeaderCell}
            didUpdate={this.headerAfterRenderCallBack}
            {...restColumnProps}
            {...subHeaderProps}
            {...subDrillDownProps}
            handleMenuClick={this.handleMenuClick}
            sourceForm={props.sourceForm}
          >
            {HeaderComponent ? <HeaderComponent {...headerComponentProps} /> : headerCell}
          </Cell>
        </Resizer>
      ) : (
        <Cell
          key={index}
          width={columnWidth}
          fnGetCellContentStyle={columnProps.fnGetHeaderCellContentStyle}
          fnGetCellStyle={columnProps.fnGetHeaderCellStyle}
          type={"header-cell"}
          deep={deep}
          columnIndex={currentColumn}
          registerCell={this.registerHeaderCell}
          unregisterCell={this.unregisterHeaderCell}
          didUpdate={this.headerAfterRenderCallBack}
          {...restColumnProps}
        >
          {HeaderComponent ? <HeaderComponent {...headerComponentProps} /> : headerCell}
        </Cell>
      );
    },
    registerHeaderGroupCell: function(headerGroupId, cellElem) {
      this.tableStore.registerHeaderGroupCell(cellElem, headerGroupId);
    },
    unregisterHeaderGroupCell: function(headerGroupId, cellElem) {
      this.tableStore.unregisterHeaderGroupCell(cellElem, headerGroupId);
    },
    registerHeaderCell: function(cellElem, columnIndex, deep) {
      this.tableStore.registerHeaderCell(cellElem, columnIndex, deep);
    },
    unregisterHeaderCell: function(cellElem, columnIndex, deep) {
      this.tableStore.unregisterHeaderCell(cellElem, columnIndex, deep);
    },
    registerCell: function(cellElem, columnIndex) {
      this.tableStore.registerCell(cellElem, columnIndex);
    },
    unregisterCell: function(cellElem, columnIndex) {
      this.tableStore.unregisterCell(cellElem, columnIndex);
    },

    registerVirtualStore: function(virtualStore) {
      this.tableStore.registerVirtualStore(virtualStore);
    },

    registerRenderCallback: function(props) {
      this.afterRenderCallBack = debounce(100, props.afterRenderCallBack || noop);

      this.headerGroupAfterRenderCallBack = debounce(
        100,
        function() {
          if (!this.skipSyncGroupHeaderHeight && !isEmpty(this.tableStore.headerGroups)) {
            this.tableStore.updateHeaderGroupHeight();
            this.afterRenderCallBack();
          }
          this.skipSyncGroupHeaderHeight = false;
        }.bind(this)
      );

      this.headerAfterRenderCallBack = debounce(
        100,
        function() {
          if (!this.skipSyncHeaderHeight && !isEmpty(this.tableStore.headerGroups)) {
            this.tableStore.updateHeaderHeight();
            this.afterRenderCallBack();
          }
          this.skipSyncHeaderHeight = false;
        }.bind(this)
      );

      this.cellAfterRenderCallBack = debounce(
        100,
        function() {
          if (this.props.stickHorizontal && !isEmpty(this.tableStore.cellElements)) {
            this.tableStore.updateCellsHeight();
            this.afterRenderCallBack();
          }
        }.bind(this)
      );
    },

    /**
     * Update header when scroll horizontal table body
     * @param  {Object} value
     */
    handleScroll: function(value) {
      var headerTableElm = this.refs.headerTable;

      if (value.target.scrollLeft >= 0) {
        headerTableElm.style.left = -1 * value.target.scrollLeft + 'px';
      }
    },

    handleColumnResize: function(index) {
      var table = this;
      var tableStore = this.tableStore;

      return function(newPos) {
        table.userResizing = true;
        tableStore.resizeColumn(index, newPos.width, true);
      };
    },

    handleResizeStop: function(index) {
      var props = this.props;
      this.userResizing = false;
      return function(columnProps) {
        var columnOpts = {
          index: index,
          columnWidth: columnProps.width
        };
        return props.onResizeStopCallback && props.onResizeStopCallback(columnOpts);
      };
    },

    getAllColumns: function(children) {
      var getAllColumnRecursive = function(childRecursive) {
        childRecursive = concat([], childRecursive);

        if (isEmpty(childRecursive)) {
          return [];
        }

        return flatMap(function(child) {
          if (!child) {
            return [];
          }
          if (child.type === ColumnGroup) {
            return getAllColumnRecursive(child.props.children);
          }
          if (child.type === Column) {
            return [child];
          }
          return [];
        }, childRecursive);
      };

      return compact(getAllColumnRecursive(children));
    },

    initColumnSizes: function(props, needUpdateColumn) {
      this.changeTableWidth(Infinity, props.children, needUpdateColumn);
    },

    changeTableWidth: function(tableWidth, columns, needUpdateColumn) {
      var cols = this.getAllColumns(columns);
      var widthInfos = map(function(col) {
        return { width: col.props.width, fixed: !!col.props.fixed };
      }, cols);

      this.tableStore.updateWidths(tableWidth, widthInfos, needUpdateColumn);
    },

    onTableWidthChanged: function(totalColumnWidth, index) {
      totalColumnWidth += this.props.draggableTable ? ResizeHandleWidth : 0;
      totalColumnWidth += this.props.rowSpan && this.props.staticRender ? this.props.rowSpan.width : 0;
      // Change width of body
      var tableBody = this.refs.tableBody;

      tableBody && $izenda(tableBody).width(totalColumnWidth); // (tableBody.style.width = totalColumnWidth + 'px');
    }
  });

  module.exports = {
    Table: observer(Table),
    Column: Column,
    ColumnGroup: ColumnGroup,
    DraggableRow: DraggableRow
  };
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/DataGrid/Izenda.Table.jsx